import React from 'react';
import { Box, Typography } from '@/shared/design-system/primitives';
import { formatMacro } from '@shared/utils/formatting';
import { DetailedMetric } from '@/shared/logic/metabolic/nutrition-compute';

interface MetabolicSummaryProps {
    totals: {
        calories: number | DetailedMetric;
        protein: number | DetailedMetric;
        carbs: number | DetailedMetric;
        fat: number | DetailedMetric;
        insulin_load?: number | DetailedMetric;
    };
}

/**
 * MetabolicSummary: Visual dashboard for session totals.
 * V4.5: Hardened to support both raw numbers and DetailedMetric objects from Deep Source Mapping.
 */
export const MetabolicSummary: React.FC<MetabolicSummaryProps> = ({ totals }) => {
    const resolveValue = (val: number | DetailedMetric | undefined) => {
        if (val === undefined) return 0;
        if (typeof val === 'object' && 'total' in val) return val.total;
        return Number(val) || 0;
    };

    const calories = resolveValue(totals.calories);
    const protein = resolveValue(totals.protein);
    const carbs = resolveValue(totals.carbs);
    const fat = resolveValue(totals.fat);
    const insulin_load = resolveValue(totals.insulin_load);

    return (
        <Box style={{ 
            marginTop: 10, padding: 20, paddingHorizontal: 10,
            backgroundColor: 'rgba(242, 155, 17, 0.03)', 
            borderRadius: 12, borderWidth: 1, borderColor: 'rgba(242, 155, 17, 0.1)' 
        } as any}>
            <Typography style={{ fontSize: 9, fontWeight: '800', color: '#f29b11', letterSpacing: 2, marginBottom: 12, opacity: 0.6, textTransform: 'uppercase' }}>
                TOTAL METABOLIC LOAD
            </Typography>
            <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Typography style={{ fontSize: 24, fontWeight: '700', color: '#fff', fontVariantNumeric: 'lining-nums' } as any}>{formatMacro(calories)}</Typography>
                    <Typography style={{ fontSize: 10, fontWeight: '900', color: '#f29b11', marginTop: 8 }}>KCAL</Typography>
                </Box>
                <Box style={{ display: 'flex', flexDirection: 'row', gap: 15 }}>
                    <Box style={{ alignItems: 'flex-end' }}>
                        <Typography style={{ fontSize: 14, fontWeight: '700', color: '#3b82f6', fontVariantNumeric: 'lining-nums' } as any}>{formatMacro(protein)}g</Typography>
                        <Typography style={{ fontSize: 8, fontWeight: '900', color: '#3b82f6', opacity: 0.6 }}>PROTEIN</Typography>
                    </Box>
                    <Box style={{ alignItems: 'flex-end' }}>
                        <Typography style={{ fontSize: 14, fontWeight: '700', color: '#2ecc71', fontVariantNumeric: 'lining-nums' } as any}>{formatMacro(carbs)}g</Typography>
                        <Typography style={{ fontSize: 8, fontWeight: '900', color: '#2ecc71', opacity: 0.6 }}>CARBS</Typography>
                    </Box>
                    <Box style={{ alignItems: 'flex-end' }}>
                        <Typography style={{ fontSize: 14, fontWeight: '700', color: '#ef4444', fontVariantNumeric: 'lining-nums' } as any}>{formatMacro(fat)}g</Typography>
                        <Typography style={{ fontSize: 8, fontWeight: '900', color: '#ef4444', opacity: 0.6 }}>FATS</Typography>
                    </Box>
                    {insulin_load !== 0 && (
                        <Box style={{ alignItems: 'flex-end' }}>
                            <Typography style={{ fontSize: 14, fontWeight: '700', color: insulin_load > 50 ? '#e74c3c' : '#f1c40f', fontVariantNumeric: 'lining-nums' } as any}>{formatMacro(insulin_load)}</Typography>
                            <Typography style={{ fontSize: 8, fontWeight: '900', color: insulin_load > 50 ? '#e74c3c' : '#f1c40f', opacity: 0.6 }}>INSULIN LOAD</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
